import React, { useCallback, useEffect, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import navConfig from "../nav-section/navConfig";
import {
  selectTourRunning,
  selectTourType,
  endTour,
} from "../../features/tour/tourSlice";

const tourDefinitions = {
  app: {
    title: "Home",
    content:
      "Hier sehen Sie Ihre wichtigsten Kennzahlen, Entwicklungen und Aktivitäten auf einen Blick.",
  },
  news: {
    title: "Neuigkeiten",
    content:
      "Alle wichtigen Updates aus Ihrem Verein, Team oder aus dem System finden Sie hier.",
  },
  user: {
    title: "Benutzer",
    content: "Verwalten Sie Benutzerkonten, Rollen und Zugänge für Ihr Team.",
  },
  tasks: {
    title: "Aufgaben",
    content:
      "Planen Sie To-dos, verfolgen Sie den Status und behalten Sie offene Aufgaben im Blick.",
  },
  sites: {
    title: "Standorte",
    content:
      "Erstellen und verwalten Sie Standorte inklusive Trainingsorte und Zuordnungen.",
  },
  members: {
    title: "Mitglieder",
    content:
      "Mitglieder anlegen, pflegen und bei Bedarf per Import schnell übernehmen.",
  },
  contracts: {
    title: "Vertragsdaten",
    content: "Alle Vertragsinformationen und Laufzeiten zentral verwalten.",
  },
  banking: {
    title: "Banking",
    content:
      "SEPA-Lastschriften vorbereiten und Zahlungsprozesse zuverlässig abwickeln.",
  },
  trainer: {
    title: "Training",
    content:
      "Trainingsgruppen und Trainingsorganisation im Tagesgeschäft verwalten.",
  },
  testings: {
    title: "Prüfungen",
    content: "Prüfungen planen, dokumentieren und Gebühren im Blick behalten.",
  },
  tournaments: {
    title: "Turniere",
    content:
      "Turniere erfassen, organisieren und relevante Informationen zentral ablegen.",
  },
  events: {
    title: "Veranstaltungen",
    content: "Veranstaltungen für Ihre Mitglieder vorbereiten und steuern.",
  },
  shop: {
    title: "Shop",
    content: "Produkte und Bestellungen zentral verwalten.",
  },
  survey: {
    title: "Befragung",
    content: "Umfragen erstellen und Antworten strukturiert auswerten.",
  },
  support: {
    title: "Support",
    content: "Support-Anfragen und Hilfethemen zentral organisieren.",
  },
  general: {
    title: "System",
    content:
      "Wichtige Grundeinstellungen wie Stammdaten, Logo und weitere Konfigurationen.",
  },
};

const orderedTourKeys = [
  "app",
  "news",
  "user",
  "tasks",
  "sites",
  "members",
  "contracts",
  "banking",
  "trainer",
  "testings",
  "tournaments",
  "events",
  "shop",
  "survey",
  "support",
  "general",
];

const getPathTourKey = (path = "") => path.split("/").filter(Boolean).pop();

const allTourSteps = orderedTourKeys
  .map((key) => {
    const navItem = navConfig.find((item) => getPathTourKey(item.path) === key);
    const stepDefinition = tourDefinitions[key];

    if (!navItem || !stepDefinition) return null;

    return {
      target: `[data-tour="${key}"]`,
      title: stepDefinition.title,
      content: stepDefinition.content,
      disableBeacon: key === "app",
      route: navItem.path,
    };
  })
  .filter(Boolean);

const locale = {
  back: "Zurück",
  close: "Fertig",
  last: "Fertig",
  next: "Weiter",
  nextLabelWithProgress: "Weiter ({step}/{steps})",
  skip: "Überspringen",
};

const GuidedTour = () => {
  const running = useSelector(selectTourRunning);
  const tourType = useSelector(selectTourType);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!running || tourType !== "admin") {
      setSteps([]);
      setStepIndex(0);
      return;
    }

    const visibleSteps = allTourSteps.filter((step) =>
      document.querySelector(step.target),
    );
    const nextSteps = visibleSteps.length ? visibleSteps : allTourSteps;

    if (!nextSteps.length) {
      dispatch(endTour());
      return;
    }

    setSteps(nextSteps);
    setStepIndex(0);
  }, [running, tourType, dispatch]);

  useEffect(() => {
    if (!running || tourType !== "admin" || !steps.length) return;
    const currentStep = steps[stepIndex];
    if (!currentStep?.route) return;

    if (
      location.pathname !== currentStep.route &&
      !location.pathname.startsWith(`${currentStep.route}/`)
    ) {
      navigate(currentStep.route);
    }
  }, [running, tourType, stepIndex, steps, location.pathname, navigate]);

  const handleCallback = useCallback(
    (data) => {
      const { status, type, action, index } = data;

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        if (status === STATUS.FINISHED) {
          navigate("/dashboard/app");
        }
        dispatch(endTour());
        return;
      }

      if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);

        if (nextIndex >= steps.length) {
          navigate("/dashboard/app");
          dispatch(endTour());
          return;
        }

        if (nextIndex < 0) {
          dispatch(endTour());
          return;
        }

        setStepIndex(nextIndex);
      }
    },
    [dispatch, navigate, steps.length],
  );

  if (!running || !steps.length || tourType === "member") return null;

  return (
    <Joyride
      steps={steps}
      run={running}
      stepIndex={stepIndex}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      locale={locale}
      callback={handleCallback}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: "#1976d2",
        },
      }}
    />
  );
};

export default GuidedTour;
